import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { JsonSettings } from "./ClickerAdminTab";

const btn = "mono text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full border transition-colors";
const err = (e) => toast.error(formatApiError(e?.response?.data?.detail) || "Failed");
const SUBS = [["dashboard", "Dashboard"], ["members", "Members"], ["channels", "Channels"], ["reports", "Reports"], ["transactions", "Transactions"], ["settings", "Settings"], ["audit", "Audit log"]];

function Members() {
  const [rows, setRows] = useState([]); const [q, setQ] = useState("");
  const load = () => api.get("/admin/walkie/members", { params: { q } }).then((r) => setRows(r.data.members));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const edit = async (uid, body) => { try { await api.post(`/admin/walkie/members/${uid}`, body); toast.success("Updated"); load(); } catch (e) { err(e); } };
  return (
    <div className="space-y-2">
      <form onSubmit={(e) => { e.preventDefault(); load(); }}><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search members" data-testid="admin-walkie-member-search" className="bg-black/40 border border-white/10 rounded-full px-3 py-1.5 text-sm" /></form>
      {rows.map((m) => (
        <div key={m.user_id} className="glass rounded-xl p-3 flex flex-wrap items-center gap-3 text-sm" data-testid={`admin-walkie-member-${m.user_id}`}>
          {m.avatar_url ? <img src={m.avatar_url} alt="" className="w-9 h-9 rounded-full object-cover" /> : <span className="w-9 h-9 rounded-full bg-white/10" />}
          {m.cover_url && <img src={m.cover_url} alt="" className="w-16 h-9 rounded object-cover" />}
          <div className="flex-1 min-w-[120px]"><b>{m.display_name}</b> <span className="text-white/40">@{m.username}</span>{m.premium && <span className="ml-2 text-[#FFE300] mono text-[10px]">PREMIUM</span>}{m.is_suspended && <span className="ml-2 text-[#FF003C] mono text-[10px]">SUSPENDED</span>}</div>
          {m.avatar_url && <button onClick={() => edit(m.user_id, { remove_avatar: true })} className={`${btn} border-white/15`}>Remove photo</button>}
          {m.cover_url && <button onClick={() => edit(m.user_id, { remove_cover: true })} className={`${btn} border-white/15`}>Remove cover</button>}
          <button onClick={() => { const v = parseInt(window.prompt("Coins to add (negative to remove)", "100"), 10); if (v) edit(m.user_id, { coins_delta: v }); }} className={`${btn} border-white/15`}>Adjust coins</button>
          <button onClick={() => edit(m.user_id, { is_suspended: !m.is_suspended })} data-testid={`admin-walkie-suspend-${m.user_id}`} className={`${btn} ${m.is_suspended ? "border-[#39FF14]/40 text-[#39FF14]" : "border-[#FF003C]/40 text-[#FF003C]"}`}>{m.is_suspended ? "Restore" : "Suspend"}</button>
        </div>
      ))}
    </div>
  );
}

function Channels() {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/admin/walkie/channels").then((r) => setRows(r.data.channels));
  useEffect(() => { load(); }, []);
  const act = (id, action) => api.post(`/admin/walkie/channels/${id}`, { action }).then(() => { toast.success("Done"); load(); }).catch(err);
  return rows.map((c) => (
    <div key={c.id} className="glass rounded-xl p-3 flex flex-wrap items-center gap-3 text-sm mb-1.5" data-testid={`admin-walkie-channel-${c.id}`}>
      <b className="flex-1">#{c.name}</b><span className="mono text-xs">{c.kind}</span><span className="mono text-xs">{c.members} members · {c.live} live</span>
      <span className="mono text-xs text-white/40">{Math.round((c.voice_seconds || 0) / 60)} min talk</span>
      <button onClick={() => act(c.id, c.is_removed ? "restore" : "remove")} className={`${btn} ${c.is_removed ? "border-[#39FF14]/40 text-[#39FF14]" : "border-[#FF003C]/40 text-[#FF003C]"}`}>{c.is_removed ? "Restore" : "Remove"}</button>
    </div>
  ));
}

function Reports() {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/admin/walkie/reports").then((r) => setRows(r.data.reports));
  useEffect(() => { load(); }, []);
  const act = (id, action) => api.post(`/admin/walkie/reports/${id}`, { action }).then(() => { toast.success("Resolved"); load(); }).catch(err);
  if (!rows.length) return <div className="text-white/40 text-sm">No open reports.</div>;
  return rows.map((r) => (
    <div key={r.id} className="glass rounded-xl p-3 text-sm mb-1.5 flex flex-wrap items-center gap-3" data-testid={`admin-walkie-report-${r.id}`}>
      <span className="mono text-xs text-[#FF003C] uppercase">{r.target_type}</span><span className="mono text-[10px] text-white/40">{r.target_id.slice(0, 8)}</span>
      <span className="flex-1">{r.reason}</span>
      <button onClick={() => act(r.id, "remove_content")} className={`${btn} border-[#FF003C]/40 text-[#FF003C]`}>{r.target_type === "user" ? "Suspend member" : "Remove content"}</button>
      <button onClick={() => act(r.id, "dismiss")} className={`${btn} border-white/15`}>Dismiss</button>
    </div>
  ));
}

function Transactions() {
  const [d, setD] = useState(null); const [flagged, setFlagged] = useState(false);
  useEffect(() => { api.get("/admin/walkie/transactions", { params: { flagged } }).then((r) => setD(r.data)); }, [flagged]);
  if (!d) return null;
  return (
    <div className="space-y-4">
      <label className="text-sm flex items-center gap-2"><input type="checkbox" checked={flagged} onChange={() => setFlagged(!flagged)} /> Suspicious tips only</label>
      <div><div className="mono text-[10px] uppercase text-white/40 mb-1">Tips</div>
        {d.tips.map((t) => <div key={t.id} className={`text-xs glass rounded-lg px-3 py-2 mb-1 flex gap-3 ${t.flagged ? "border border-[#FF003C]/40" : ""}`}><span className="flex-1">{t.from_name} → {t.to_name}</span><b>{t.coins}</b><span className="text-white/40">{t.status}</span><span className="text-white/40">{t.created_at.slice(0, 16)}</span></div>)}
      </div>
      <div><div className="mono text-[10px] uppercase text-white/40 mb-1">Stripe purchases</div>
        {d.purchases.map((p) => <div key={p.session_id} className="text-xs glass rounded-lg px-3 py-2 mb-1 flex gap-3"><span className="flex-1">{p.user_email} · {p.lookup_key}</span><b>${p.amount}</b><span className={p.payment_status === "paid" ? "text-[#39FF14]" : "text-white/40"}>{p.payment_status}{p.fulfilled ? " · credited" : ""}</span></div>)}
      </div>
    </div>
  );
}

function Dashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/walkie/dashboard").then((r) => setD(r.data)); }, []);
  if (!d) return null;
  return <div className="grid grid-cols-2 md:grid-cols-4 gap-3">{Object.entries(d).map(([k, v]) => <div key={k} className="glass rounded-xl p-3" data-testid={`admin-walkie-stat-${k}`}><div className="mono text-[10px] uppercase text-white/40">{k.replace("_", " ")}</div><div className="font-black text-2xl">{v}</div></div>)}</div>;
}

function Audit() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/walkie/audit").then((r) => setRows(r.data.audit)); }, []);
  return rows.map((a) => <div key={a.id} className="text-xs glass rounded-lg px-3 py-2 mb-1 flex gap-3"><span className="text-white/40">{a.at.slice(0, 16)}</span><span>{a.admin_email}</span><b>{a.action}</b><span className="text-white/50 truncate">{a.target} {a.note}</span></div>);
}

export default function WalkieAdminTab() {
  const [sub, setSub] = useState("dashboard");
  return (
    <div className="space-y-4" data-testid="admin-walkie-tab">
      <div className="flex flex-wrap gap-2">
        {SUBS.map(([k, l]) => <button key={k} onClick={() => setSub(k)} data-testid={`admin-walkie-sub-${k}`} className={`${btn} ${sub === k ? "border-[#FF2BD6] text-[#FF2BD6]" : "border-white/10 text-white/50"}`}>{l}</button>)}
      </div>
      {sub === "dashboard" && <Dashboard />}
      {sub === "members" && <Members />}
      {sub === "channels" && <Channels />}
      {sub === "reports" && <Reports />}
      {sub === "transactions" && <Transactions />}
      {sub === "settings" && <><p className="text-xs text-white/50">Coin amounts per Stripe package, shop item prices, premium coin price, tip limits, channel limits and talk-time caps.</p><JsonSettings url="/admin/walkie/settings" testId="admin-walkie-settings" /></>}
      {sub === "audit" && <Audit />}
    </div>
  );
}
